import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Divider,
  Input,
  Select,
  Space,
  Switch,
  Tag,
} from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import {
  stockReconciliation,
  unwrapStockReconciliationRows,
  useStockReconciliationRows,
} from '@/api/StockReconciliation';
import {
  faCar,
  faCircleCheck,
  faDatabase,
  faDownload,
  faEllipsisV,
  faFileLines,
  faPercent,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { useTranslate } from '@/translations';
import { StockReconciliationResult, StockReconciliationRow } from '@/types';
import {
  CustomColumns,
  CustomTable,
  Icon,
  PageContainer,
  TooltipIcon,
} from '@/ui/components';
import {
  CUSTOMER_STOCK_SOURCE_FIELDS,
  sourceFieldValue,
} from './customerStockFields';
import { exportStockReconciliation } from './exportResult';
import {
  Breadcrumb,
  BreadcrumbLink,
  DiscrepancyGrid,
  EmptyCell,
  Filters,
  KpiGrid,
  KpiMetrics,
  MetricCard,
  MetricIcon,
  MetricBody,
  MetricLabel,
  MetricValue,
  NotesCard,
  NotesList,
  NotesTitle,
  PageHeader,
  PageSubtitle,
  PageTitle,
  ReconstructionBar,
  ReconstructionLabel,
  ReconstructionValue,
  ResultCell,
  StatusCell,
  SummaryGrid,
  TableToolbar,
  metricTone,
  palette,
} from './StockReconciliation.styled';

type StockReconciliationResultProps = {
  result: StockReconciliationResult;
  onBack: () => void;
};

const RESULT_STYLE: Record<
  StockReconciliationRow['result'],
  { icon: IconDefinition; color: string }
> = {
  Matched: { icon: faCircleCheck, color: palette.matched },
  CustomerOnly: { icon: faTriangleExclamation, color: palette.warning },
  OneViewOnly: { icon: faCar, color: palette.danger },
};

const statusColor = (value?: string) => {
  const status = (value ?? '').trim().toLowerCase();
  if (status === 'on site') return palette.statusOnSite;
  if (status === 'awaiting customer') return palette.statusAwaiting;
  if (status === 'not in oneview') return palette.statusNotIn;
  if (status === 'in transit') return palette.statusTransit;
  return undefined;
};

const formatCount = (value?: number) => Number(value || 0).toLocaleString();

const empty = (value?: string) => value || <EmptyCell>—</EmptyCell>;

export const StockReconciliationResultView: React.FC<
  StockReconciliationResultProps
> = ({ result, onBack }) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>();
  const [location, setLocation] = useState<string>();
  const [discrepanciesOnly, setDiscrepanciesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>();
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>();
  const serverRows = Boolean(result.id);
  const { data: rowPage, isFetching: rowsLoading } = useStockReconciliationRows(
    result.id,
    {
      skip: (page - 1) * pageSize,
      take: pageSize,
      sortField,
      sortOrder,
      search: search.trim() || undefined,
      status,
      location,
      discrepanciesOnly,
    },
    { enabled: serverRows },
  );

  const reconstruction = result.reconstruction ?? {
    snapshotStock: 0,
    arrivals: 0,
    dispatches: 0,
    reconstructedStock: 0,
  };
  const counts = result.counts ?? {
    customerReport: 0,
    oneViewStock: 0,
    matched: 0,
    matchRate: 0,
    customerOnly: 0,
    oneViewOnly: 0,
    dupedInSource: 0,
    dupedInOneView: 0,
  };
  const localRows = useMemo(() => result.rows ?? [], [result.rows]);
  const notes = result.notes ?? [];

  const at = dayjs(result.reconciliationAt).format('DD MMM YYYY hh A');
  const snapshotUsed = reconstruction.snapshotUsed
    ? dayjs(reconstruction.snapshotUsed).format('DD MMM YYYY hh A')
    : '—';

  const statuses = useMemo(
    () =>
      serverRows
        ? (rowPage?.statuses ?? [])
        : [
            ...new Set(
              localRows.map((row) => row.oneViewStatus).filter(Boolean),
            ),
          ],
    [localRows, rowPage?.statuses, serverRows],
  );
  const locations = useMemo(
    () =>
      serverRows
        ? (rowPage?.locations ?? [])
        : [
            ...new Set(
              localRows.map((row) => row.oneViewLocation).filter(Boolean),
            ),
          ],
    [localRows, rowPage?.locations, serverRows],
  );

  const filteredRows = useMemo(() => {
    if (serverRows) return rowPage?.items ?? [];
    const query = search.trim().toLowerCase();
    return localRows.filter((row) => {
      if (discrepanciesOnly && row.result === 'Matched') return false;
      if (status && row.oneViewStatus !== status) return false;
      if (location && row.oneViewLocation !== location) return false;
      if (!query) return true;
      const haystack = [
        row.vin,
        row.oneViewMake,
        row.oneViewModel,
        row.oneViewLocation,
        ...CUSTOMER_STOCK_SOURCE_FIELDS.map(
          (field) => sourceFieldValue(row.source, field) ?? '',
        ),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [
    discrepanciesOnly,
    localRows,
    location,
    rowPage?.items,
    search,
    serverRows,
    status,
  ]);

  useEffect(() => {
    setPage(1);
  }, [discrepanciesOnly, location, search, status]);

  const totalRows = serverRows ? (rowPage?.total ?? 0) : filteredRows.length;

  const columns: CustomColumns<StockReconciliationRow> = [
    {
      title: t('stockReconciliationResult'),
      dataIndex: 'result',
      sorter: () => 0,
      fixed: 'left',
      width: 150,
      render: (_, row) => {
        const style = RESULT_STYLE[row.result] ?? RESULT_STYLE.Matched;
        return (
          <Space size={4} wrap>
            <ResultCell $color={style.color}>
              <Icon icon={style.icon} color={style.color} />
              {t(`stockReconciliation${row.result}`)}
            </ResultCell>
            {row.dupedInSource ? (
              <Tag>{t('stockReconciliationDupedInSource')}</Tag>
            ) : null}
            {row.dupedInOneView ? (
              <Tag>{t('stockReconciliationDupedInOneView')}</Tag>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: t('stockReconciliationVin'),
      dataIndex: 'vin',
      sorter: () => 0,
      fixed: 'left',
      width: 220,
    },
    ...CUSTOMER_STOCK_SOURCE_FIELDS.map(
      (field): CustomColumns<StockReconciliationRow>[number] => ({
        title: t(field.titleKey),
        dataIndex:
          field.sortField as CustomColumns<StockReconciliationRow>[number]['dataIndex'],
        sorter: () => 0,
        width: field.width,
        render: (_: unknown, row: StockReconciliationRow) =>
          empty(sourceFieldValue(row.source, field)),
      }),
    ),
    {
      title: t('stockReconciliationOneViewMake'),
      dataIndex: 'oneViewMake',
      sorter: () => 0,
      width: 150,
      render: (value: string) => empty(value),
    },
    {
      title: t('stockReconciliationOneViewModel'),
      dataIndex: 'oneViewModel',
      sorter: () => 0,
      width: 250,
      render: (value: string) => empty(value),
    },
    {
      title: t('stockReconciliationOneViewYear'),
      dataIndex: 'oneViewYear',
      sorter: () => 0,
      width: 80,
      render: (value: string) => empty(value),
    },
    {
      title: t('stockReconciliationOneViewLocation'),
      dataIndex: 'oneViewLocation',
      sorter: () => 0,
      width: 150,
      render: (value: string) => empty(value),
    },
    {
      title: t('stockReconciliationOneViewArrived'),
      dataIndex: 'oneViewArrived',
      sorter: () => 0,
      width: 180,
      render: (value: string) =>
        value ? dayjs(value).format('DD MMM YYYY HH:mm') : empty(),
    },
    {
      title: t('stockReconciliationOneViewStatus'),
      dataIndex: 'oneViewStatus',
      sorter: () => 0,
      width: 120,
      render: (value: string) =>
        value ? (
          <StatusCell $color={statusColor(value)}>{value}</StatusCell>
        ) : (
          empty()
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'vin',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: () => <TooltipIcon icon={faEllipsisV} onClick={() => {}} />,
    },
  ];

  const summaryCards = [
    {
      key: 'customer',
      tone: 'navy' as const,
      icon: faCar,
      label: t('stockReconciliationCustomerStock', {
        customer: result.customerName,
      }),
      value: formatCount(counts.customerReport),
    },
    {
      key: 'oneView',
      tone: 'navy' as const,
      icon: faDatabase,
      label: t('stockReconciliationOneViewStock'),
      value: formatCount(counts.oneViewStock),
    },
    {
      key: 'matched',
      tone: 'matched' as const,
      icon: faCircleCheck,
      label: t('stockReconciliationMatched'),
      value: formatCount(counts.matched),
    },
    {
      key: 'rate',
      tone: 'navy' as const,
      icon: faPercent,
      label: t('stockReconciliationMatchRate'),
      value: `${Number(counts.matchRate).toFixed(1)}%`,
    },
  ];

  const discrepancyCards = [
    {
      key: 'customerOnly',
      tone: 'warning' as const,
      icon: faTriangleExclamation,
      label: t('stockReconciliationCustomerOnly'),
      value: formatCount(counts.customerOnly),
    },
    {
      key: 'oneViewOnly',
      tone: 'danger' as const,
      icon: faCar,
      label: t('stockReconciliationOneViewOnly'),
      value: formatCount(counts.oneViewOnly),
    },
    {
      key: 'duplicates',
      tone: 'default' as const,
      icon: faFileLines,
      label: t('stockReconciliationDuplicates'),
      value: formatCount(counts.dupedInSource),
    },
  ];

  const exportRows = async () => {
    if (!serverRows || !result.id) {
      exportStockReconciliation(result, filteredRows);
      return;
    }
    const query = {
      sortField,
      sortOrder,
      search: search.trim() || undefined,
      status,
      location,
      discrepanciesOnly,
      exportAll: true,
    };
    setExporting(true);
    try {
      const take = 100_000;
      const collected: StockReconciliationRow[] = [];
      let skip = 0;
      let total = 0;
      do {
        const page = unwrapStockReconciliationRows(
          await stockReconciliation.rows(result.id, { ...query, skip, take }),
        );
        collected.push(...page.items);
        total = page.total;
        skip += page.items.length;
        if (!page.items.length) break;
      } while (collected.length < total);
      exportStockReconciliation(result, collected);
    } catch {
      message.error(t('stockReconciliationExportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter:
      | SorterResult<StockReconciliationRow>
      | SorterResult<StockReconciliationRow>[],
  ) => {
    const sort = Array.isArray(sorter) ? sorter[0] : sorter;
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? pageSize);
    setSortField(sort?.order ? String(sort.field) : undefined);
    setSortOrder(sort?.order ?? undefined);
  };

  return (
    <PageContainer>
      <PageHeader>
        <Breadcrumb>
          <BreadcrumbLink type="button" onClick={onBack}>
            {t('stockReconciliation')}
          </BreadcrumbLink>{' '}
          / {result.customerName} / {result.locationName} / {at}
        </Breadcrumb>
        <Button onClick={onBack}>{t('back')}</Button>
      </PageHeader>

      <div>
        <PageTitle>
          {result.customerName} {t('stockReconciliation')}
        </PageTitle>
        <PageSubtitle>{t('stockReconciliationSubtitle')}</PageSubtitle>
        <ReconstructionBar>
          <span>
            <ReconstructionLabel>
              {t('stockReconciliationSnapshotUsed')}:{' '}
            </ReconstructionLabel>
            <ReconstructionValue>{snapshotUsed}</ReconstructionValue>
          </span>
          <Divider type="vertical" />
          <span>
            <ReconstructionLabel>
              {t('stockReconciliationSnapshotStock')}:{' '}
            </ReconstructionLabel>
            <ReconstructionValue>
              {formatCount(reconstruction.snapshotStock)}
            </ReconstructionValue>
          </span>
          <Divider type="vertical" />
          <span>
            <ReconstructionLabel>
              {t('stockReconciliationArrivals')}:{' '}
            </ReconstructionLabel>
            <ReconstructionValue>
              +{formatCount(reconstruction.arrivals)}
            </ReconstructionValue>
          </span>
          <Divider type="vertical" />
          <span>
            <ReconstructionLabel>
              {t('stockReconciliationDispatches')}:{' '}
            </ReconstructionLabel>
            <ReconstructionValue>
              -{formatCount(reconstruction.dispatches)}
            </ReconstructionValue>
          </span>
          <Divider type="vertical" />
          <span>
            <ReconstructionLabel>
              {t('stockReconciliationReconstructedStock')}:{' '}
            </ReconstructionLabel>
            <ReconstructionValue>
              {formatCount(reconstruction.reconstructedStock)}
            </ReconstructionValue>
          </span>
        </ReconstructionBar>
      </div>

      <KpiGrid>
        <KpiMetrics>
          <SummaryGrid>
            {summaryCards.map((card) => (
              <MetricCard key={card.key}>
                <MetricIcon $tone={card.tone}>
                  <Icon icon={card.icon} color={metricTone[card.tone].icon} />
                </MetricIcon>
                <MetricBody>
                  <MetricLabel>{card.label}</MetricLabel>
                  <MetricValue $tone={card.tone}>{card.value}</MetricValue>
                </MetricBody>
              </MetricCard>
            ))}
          </SummaryGrid>
          <DiscrepancyGrid>
            {discrepancyCards.map((card) => (
              <MetricCard key={card.key}>
                <MetricIcon $tone={card.tone}>
                  <Icon icon={card.icon} color={metricTone[card.tone].icon} />
                </MetricIcon>
                <MetricBody>
                  <MetricLabel>{card.label}</MetricLabel>
                  <MetricValue $tone={card.tone}>{card.value}</MetricValue>
                </MetricBody>
              </MetricCard>
            ))}
          </DiscrepancyGrid>
        </KpiMetrics>
        <NotesCard>
          <NotesTitle>
            <Icon icon={faFileLines} color={palette.notesTitle} />
            {t('stockReconciliationNotes')}
          </NotesTitle>
          <NotesList>
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </NotesList>
        </NotesCard>
      </KpiGrid>

      <Card>
        <TableToolbar>
          <Filters>
            <Input.Search
              allowClear
              placeholder={t('stockReconciliationSearch')}
              onSearch={setSearch}
              onChange={(event) => {
                if (!event.target.value) setSearch('');
              }}
              style={{ width: 280 }}
            />
            <Select
              allowClear
              placeholder={t('stockReconciliationAllStatuses')}
              options={statuses.map((value) => ({ value, label: value }))}
              value={status}
              onChange={setStatus}
              style={{ minWidth: 160 }}
            />
            <Select
              allowClear
              placeholder={t('stockReconciliationAllLocations')}
              options={locations.map((value) => ({ value, label: value }))}
              value={location}
              onChange={setLocation}
              style={{ minWidth: 160 }}
            />
            <Space>
              <Switch
                checked={discrepanciesOnly}
                onChange={setDiscrepanciesOnly}
              />
              {t('stockReconciliationDiscrepanciesOnly')}
            </Space>
          </Filters>
          <Button
            icon={<Icon icon={faDownload} />}
            loading={exporting}
            onClick={exportRows}
          >
            {t('stockReconciliationExport')}
          </Button>
        </TableToolbar>
        <CustomTable
          rowKey="vin"
          columns={columns}
          dataSource={filteredRows}
          loading={serverRows && rowsLoading}
          scroll={{ x: 1900 }}
          pagination={{
            current: page,
            pageSize,
            total: totalRows,
            showSizeChanger: true,
          }}
          onChange={onTableChange}
        />
      </Card>
    </PageContainer>
  );
};
